export const profileService = {
  fetchProfile: async (apiCall, userId) => {
    try {
      const response = await apiCall(`/api/profile/${userId}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP error! status: ${response.status}`,
        );
      }

      return data.data || data.profile || data;
    } catch (error) {
      throw new Error(`Failed to fetch profile: ${error.message}`);
    }
  },

  // Fetch current user's profile
  fetchMyProfile: async (apiCall) => {
    try {
      const response = await apiCall("/api/profile/me");
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP error! status: ${response.status}`,
        );
      }

      const profileData = data.data;

      const normalizedProfile = {
        username: profileData.username || "",
        name: profileData.name || "",
        email: profileData.email || "",
        bio: profileData.bio || "",
        verified: profileData.verified,
        profileImage:
          profileData.profileImage || profileData.profilePicture || null,
        location: profileData.location || "",
        website: profileData.website || "",
        company: profileData.company || profileData.currentCompany || "",
        position: profileData.position || "",
        experience:
          profileData.experience ||
          (profileData.yearOfExperience
            ? `${profileData.yearOfExperience}+ years`
            : ""),
        education: profileData.education || "",
        gender: profileData.gender || "",
        birthday: profileData.birthday
          ? profileData.birthday.split("T")[0]
          : "",
        social: {
          x: profileData.social?.x || profileData.socialLinks?.x || "",
          instagram:
            profileData.social?.instagram ||
            profileData.socialLinks?.instagram ||
            "",
        },
      };

      return normalizedProfile;
    } catch (error) {
      throw new Error(`Failed to fetch your profile: ${error.message}`);
    }
  },
  updateProfile: async (apiCall, changedFields) => {
    try {
      const transformedFields = {};

      Object.keys(changedFields).forEach((key) => {
        if (key.startsWith("social.")) {
          const platform = key.split(".")[1];
          if (!transformedFields.social) {
            transformedFields.social = {};
          }
          transformedFields.social[platform] = changedFields[key];
        } else {
          transformedFields[key] = changedFields[key];
        }
      });

      const response = await apiCall("/api/profile/update", {
        method: "PUT",
        body: JSON.stringify(transformedFields),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors && Array.isArray(data.errors)) {
          const fieldErrors = {};
          data.errors.forEach((err) => {
            const fieldName = err.path || err.param || "general";
            fieldErrors[fieldName] = err.msg || err.message;
          });

          const error = new Error(data.message || "Validation failed");
          error.fieldErrors = fieldErrors;
          error.isValidationError = true;
          throw error;
        }

        throw new Error(
          data.message || `HTTP error! status: ${response.status}`,
        );
      }

      const changedData = data.data || {};

      const transformedResponse = {};

      for (const [key, value] of Object.entries(changedData)) {
        if (key === "social" && typeof value === "object") {
          transformedResponse.social = {
            x: value.x || "",
            instagram: value.instagram || "",
          };
        } else if (key === "privacySettings" && typeof value === "object") {
          transformedResponse.privacySettings = value;
        } else if (key === "experience") {
          if (typeof value === "number") {
            transformedResponse.experience = `${value}+ years`;
          } else if (typeof value === "string") {
            const num = parseInt(value);
            transformedResponse.experience = isNaN(num)
              ? value
              : `${num}+ years`;
          }
        } else {
          transformedResponse[key] = value;
        }
      }

      return transformedResponse;
    } catch (error) {
      if (error.isValidationError) {
        throw error;
      }
      throw new Error(`Failed to update profile: ${error.message}`);
    }
  },
  fetchProfileByUsername: async (apiCall, username) => {
    try {
      const response = await apiCall(`/api/profile/${username}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP error! status: ${response.status}`,
        );
      }

      const profileData = data.data;

      // ADD THIS — the backend sends a minimal shape when blocked;
      // map it explicitly instead of falling through the full mapping below.
      if (profileData.isBlocked) {
        return {
          profileID: profileData.profileID,
          username: profileData.username || "",
          isBlocked: true,
          blockedByMe: profileData.blockedByMe,
        };
      }

      return {
        profileID: profileData.profileID,
        name: profileData.fullName || profileData.name || "",
        username: profileData.username || "",
        bio: profileData.bio || "",
        website: profileData.website || "",
        avatar: profileData.profileImage,
        followers: profileData.followers || 0,
        following: profileData.following || 0,
        admirers: profileData.admirers || 0,
        totalPosts: profileData.totalPosts || 0,
        verified: profileData.verified,
        isOwnProfile: profileData.isOwnProfile,
        followStatus: profileData.followStatus || null,
        admireStatus: profileData.admireStatus || null,
        privacySettings: profileData.privacySettings,
        email: profileData.email || null,
        birthday: profileData.birthday || null,
        isBlocked: false, 
      };
    } catch (error) {
      throw error;
    }
  },
  fetchProfileByPostId: async (apiCall, postId) => {
    try {
      // Call backend endpoint that gets post author's profile
      const response = await apiCall(`/api/posts/${postId}/author-profile`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP error! status: ${response.status}`,
        );
      }

      const profileData = data.data;

      // SAME mapping as fetchProfileByUsername
      return {
        profileID: profileData.profileID,
        name: profileData.fullName || profileData.name || "",
        username: profileData.username || "",
        bio: profileData.bio || "",
        website: profileData.website || "",
        avatar: profileData.profileImage,

        followers: profileData.followers || 0,
        following: profileData.following || 0,
        admirers: profileData.admirers || 0,
        totalPosts: profileData.totalPosts || 0,

        verified: profileData.verified,

        isOwnProfile: profileData.isOwnProfile,

        followStatus: profileData.followStatus || null,
        admireStatus: profileData.admireStatus || null,

        privacySettings: profileData.privacySettings,
        email: profileData.email || null,
        birthday: profileData.birthday || null,
      };
    } catch (error) {
      throw error;
    }
  },
  // Upload profile image
  uploadProfileImage: async (apiCall, imageFile) => {
    try {
      const formData = new FormData();
      formData.append("profileImage", imageFile);

      const response = await apiCall("/api/profile/upload-image", {
        method: "POST",
        headers: {},
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to upload image");
      }

      return data.data?.profilePicture || {};
    } catch (error) {
      throw new Error(`Failed to upload profile image: ${error.message}`);
    }
  },

  // Update password with detailed error handling
  updatePassword: async (apiCall, currentPassword, newPassword) => {
    try {
      const response = await apiCall("/api/auth/change-password", {
        method: "PUT",
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 401) {
          throw new Error(data.message || "Current password is incorrect");
        }

        if (response.status === 400) {
          throw new Error(data.message || "Invalid password format");
        }

        throw new Error(data.message || "Failed to update password");
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // Check username availability
  checkUsernameAvailability: async (apiCall, username) => {
    try {
      const response = await apiCall(
        `/api/profile/check-username?username=${encodeURIComponent(username)}`,
        {
          method: "GET",
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to check username");
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  fetchSuggestedUsers: async (apiCall) => {
    try {
      const response = await apiCall("/api/profile/suggested-users");
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP error! status: ${response.status}`,
        );
      }

      const suggestedUsers = (data.data || []).map((user) => ({
        id: user._id,
        username: user.username || "",
        profileImage: user.profilePicture || null,
        name: user.name || null,
        isVerified: user.isVerified || null,
      }));

      return suggestedUsers;
    } catch (error) {
      throw new Error(`Failed to fetch suggested users: ${error.message}`);
    }
  },
};

export const PROFILE_QUERY_KEYS = {
  profile: (userId) => ["profile", userId],
  myProfile: () => ["profile", "me"],
  checkUsername: (username) => ["username", username],
};
